import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

// ─── In-flight GET dedupe (Feb 2026 performance pass) ───────────────
// Many components independently call `api.get("/settings")` on first
// paint. Before this, a fresh home-page load fired the same request
// 16 times, saturating the network queue and pushing the nav-logo /
// hero-poster / brand-logo requests to the back.
//
// This wraps `api.get` for /settings and /brands so concurrent
// callers share one in-flight promise. Cache is cleared as soon as
// the response resolves (or rejects) — this is de-duplication, not
// caching. Subsequent gets after resolution issue fresh requests
// exactly as before.
const _inflight = new Map();
const _origGet = api.get.bind(api);
const _dedupePaths = new Set(["settings", "brands"]);
api.get = (url, config) => {
  const bare = (url || "").replace(/^\/+/, "").split(/[?#]/)[0];
  if (config || !_dedupePaths.has(bare)) return _origGet(url, config);
  if (_inflight.has(bare)) return _inflight.get(bare);
  const p = _origGet(url).finally(() => _inflight.delete(bare));
  _inflight.set(bare, p);
  return p;
};

export const formatAWG = (n) => {
  // SEO prerender (Phase 2): when the postbuild prerender captures static
  // HTML, volatile commercial numbers must never be baked into the
  // snapshot — prices always come from the live CMS via client-side fetch.
  if (typeof window !== "undefined" && window.__PRERENDER_SEO__) return "";
  return new Intl.NumberFormat("en-US", {
    style: "decimal",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(Number(n || 0));
};

// Aruba's currency is officially pegged to the USD.
// This module-level variable is the runtime rate; it is hydrated from the
// singleton settings doc during app boot (see hydrateAwgRate below). The
// default of 1.79 is used as a safe fallback if settings are unreachable.
export const DEFAULT_AWG_PER_USD = 1.79;
let _awgPerUsd = DEFAULT_AWG_PER_USD;

export const getAwgPerUsd = () => _awgPerUsd;
export const setAwgPerUsd = (rate) => {
  const n = Number(rate);
  if (n && n > 0) _awgPerUsd = n;
};

/**
 * Fetch the AWG→USD rate from the singleton settings doc and cache it in-module.
 * Called once at app boot; safe to call multiple times.
 */
export const hydrateAwgRate = async () => {
  try {
    const { data } = await api.get("/settings");
    if (data && data.awg_to_usd_rate) setAwgPerUsd(data.awg_to_usd_rate);
  } catch (_) {
    // fallback to DEFAULT_AWG_PER_USD (already set)
  }
};

// Back-compat named export used in a few places
export const AWG_PER_USD = DEFAULT_AWG_PER_USD;

export const formatUSD = (awgAmount) => {
  if (typeof window !== "undefined" && window.__PRERENDER_SEO__) return "";
  return new Intl.NumberFormat("en-US", {
    style: "decimal",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(Math.round(Number(awgAmount || 0) / _awgPerUsd));
};

// Convenience string "≈ $1,005 USD"
export const usdApprox = (awgAmount) =>
  (typeof window !== "undefined" && window.__PRERENDER_SEO__) ? "" : `≈ $${formatUSD(awgAmount)} USD`;
